import { useEffect, useState } from "react";
import { Check, Loader2, Mail, Reply, RotateCcw, Send, UserRound } from "lucide-react";
import { REPLY_TEMPLATES } from "@/components/replyTemplates";
import { CustomerPanel } from "@/components/CustomerPanel";

const when = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function ReplyBox({ m, onUpdate, admin }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const send = async () => {
    setBusy(true); setErr(null);
    try { const { data } = await admin.post(`/support/${m.id}/reply`, { message: text }); onUpdate(data); setText(""); }
    catch (x) { const d = x.response?.data?.detail; setErr(typeof d === "string" ? d : "Couldn't send the reply."); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid={`admin-support-replybox-${m.id}`}>
      <select value="" onChange={(e) => { const t = REPLY_TEMPLATES.find((x) => x.id === e.target.value); if (t) setText(t.text); }} className="bg-black border border-[#2a2f3d] px-2 py-1 text-xs" data-testid={`admin-support-template-select-${m.id}`}>
        <option value="">Insert a template…</option>
        {REPLY_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={`Reply to ${m.name}…`} className="w-full bg-black border border-[#2a2f3d] px-3 py-2 text-sm outline-none focus:border-[#e69d27]" data-testid={`admin-support-reply-input-${m.id}`} />
      {err && <p className="text-xs text-[#ff453a]" data-testid={`admin-support-reply-error-${m.id}`}>{err}</p>}
      <button onClick={send} disabled={busy || !text.trim()} className="btn-amber px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`admin-support-reply-send-${m.id}`}>
        {busy ? <Loader2 className="spin" size={12} /> : <Send size={12} />} Send reply to {m.email}
      </button>
    </div>
  );
}

const Replies = ({ m }) => (m.replies || []).map((r, i) => (
  <div key={i} className="ml-4 border-l-2 border-[#e69d27] pl-3 text-sm text-[#c9ced8] whitespace-pre-wrap" data-testid={`admin-support-sent-reply-${m.id}-${i}`}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-[#e69d27]">you replied · {when(r.at)}</div>{r.message}
  </div>
));

function Msg({ m, onUpdate, admin }) {
  const [replying, setReplying] = useState(false);
  const [showCustomer, setShowCustomer] = useState(false);
  const done = m.status === "done";
  const toggle = async () => { const { data } = await admin.patch(`/support/${m.id}`, { status: done ? "new" : "done" }); onUpdate(data); };
  return (
    <div className={`border border-[#2a2f3d] bg-[#0e1013] p-4 space-y-2 ${done && !replying && !showCustomer ? "opacity-60" : ""}`} data-testid={`admin-support-msg-${m.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-sm text-white">{m.name}</span>
        <a href={`mailto:${m.email}`} className="text-[#e69d27] hover:underline flex items-center gap-1" data-testid={`admin-support-reply-${m.id}`}><Mail size={12} />{m.email}</a>
        <span className="chip">{m.topic}</span><span className="chip">{m.plugin}</span>
        {!m.emailed && <span className="chip text-[#ff453a] border-[#ff453a]">email not sent</span>}
        <span className="ml-auto font-mono text-[#5c6479]">{when(m.created_at)}</span>
        <button onClick={() => setShowCustomer((v) => !v)} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1" data-testid={`admin-support-customer-btn-${m.id}`}><UserRound size={12} /> customer</button>
        <button onClick={() => setReplying((v) => !v)} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1" data-testid={`admin-support-reply-btn-${m.id}`}><Reply size={12} /> reply</button>
        <button onClick={toggle} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1" data-testid={`admin-support-toggle-${m.id}`}>
          {done ? <><RotateCcw size={12} /> reopen</> : <><Check size={12} /> done</>}
        </button>
      </div>
      <p className="text-sm text-[#c9ced8] whitespace-pre-wrap">{m.message}</p>
      {showCustomer && <CustomerPanel email={m.email} admin={admin} />}
      <Replies m={m} />
      {replying && <ReplyBox m={m} admin={admin} onUpdate={(d) => { onUpdate(d); setReplying(false); }} />}
    </div>
  );
}

export const AdminSupport = ({ admin }) => {
  const [list, setList] = useState(null);
  useEffect(() => { admin.get("/support").then((r) => setList(r.data)).catch(() => setList([])); }, [admin]);
  const update = (d) => setList((l) => l.map((x) => (x.id === d.id ? d : x)));
  const open = list?.filter((m) => m.status !== "done").length ?? 0;
  return (
    <section className="space-y-3" data-testid="admin-support">
      <h2 className="font-display uppercase text-2xl">Support messages <span className="text-[#e69d27]" data-testid="admin-support-open-count">{open}</span> <span className="text-sm text-[#5c6479]">open</span></h2>
      {list?.length === 0 && <p className="text-sm text-[#5c6479]" data-testid="admin-support-empty">No messages yet.</p>}
      {list?.map((m) => <Msg key={m.id} m={m} onUpdate={update} admin={admin} />)}
    </section>
  );
};
